import { signOut } from "aws-amplify/auth"
import type { NavigateFunction } from "react-router-dom"

import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config"

import { BrowserStorageType } from "@front-shared/config/general.config"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper"
import { ToastrHelper } from "@front-shared/helpers/toastr.helper"

import { GENERAL_CONFIG } from "../config/general.config"
import { ROUTES_CONFIG } from "../config/routes.config"
import { AuthService } from "../services/auth.service"
import { authReset } from "../store/slices/auth.slice"
import { billerGroupsReset } from "../store/slices/billerGroups.slice"
import { checkerReset } from "../store/slices/checker.slice"
import { coverageOverridesReset } from "../store/slices/coverageOverrides.slice"
import { coveragePayersReset } from "../store/slices/coveragePayers.slice"
import { organizationReset } from "../store/slices/organization.slice"
import { policiesReset } from "../store/slices/policies.slice"
import { runtimeReset } from "../store/slices/runtime.slice"
import { userDetailsReset } from "../store/slices/userDetails.slice"
import type { AppDispatch } from "../store/store"
import { AmplitudeHelper } from "./amplitude.helper"
import { AuthHelper } from "./auth.helper"
import { ProduktlyHelper } from "./produktly.helper"
import { SocketsHelper } from "./sockets.helper"
import { UtilHelper } from "./util.helper"

export class LogoutHelper {
    /**
     * Logout user
     */
    static logout(
        dispatch: AppDispatch | null,
        redirectUrl: string = ROUTES_CONFIG.loginUrl,
        navigate?: NavigateFunction,
        needForceResetMFA = false,
        flashMessage?: string,
    ) {
        const isDemoSession = AuthHelper.isDemoSessionActive()

        const finishLogout = (urlToRedirect: string) => {
            AuthService.clearAuthCache()

            AmplitudeHelper.clearUser()

            ProduktlyHelper.clearUser()

            try {
                sessionStorage.clear()

                // Since we are clearing all session storage, we need to keep force MFA reset variable if it's set
                if (needForceResetMFA) {
                    BrowserStorageHelper.set(
                        GENERAL_CONFIG.browserStorageKeys.authForceResetMFA,
                        true,
                        BrowserStorageType.sessionStorage,
                    )
                }
            } catch (e) {}

            // Clear saved filters in local storage
            try {
                Object.values(PoliciesListType).forEach(viewType => {
                    BrowserStorageHelper.remove(
                        viewType +
                            GENERAL_CONFIG.browserStorageKeys
                                .policiesFiltersConfigurationSuffix,
                        BrowserStorageType.localStorage,
                    )
                })
            } catch (e) {}

            // Clear saved sort in local storage
            try {
                Object.values(PoliciesListType).forEach(viewType => {
                    BrowserStorageHelper.remove(
                        viewType +
                            GENERAL_CONFIG.browserStorageKeys
                                .policiesSortConfigurationSuffix,
                        BrowserStorageType.localStorage,
                    )
                })
            } catch (e) {}

            AuthHelper.removeEagleEyeViewAsData(false)

            // Clear socket.io connection
            const socketInstance = SocketsHelper.getInstance()
            socketInstance.disconnect(true)

            if (dispatch) {
                dispatch(authReset())
                dispatch(billerGroupsReset())
                dispatch(checkerReset())
                dispatch(coverageOverridesReset())
                dispatch(coveragePayersReset())
                dispatch(organizationReset())
                dispatch(policiesReset())
                dispatch(runtimeReset())
                dispatch(userDetailsReset())
            }

            // If no dispatch, we will just redirect with reload
            UtilHelper.redirectTo(
                UtilHelper.setFlashMessage(urlToRedirect, flashMessage),
                dispatch ? navigate : undefined,
            )
        }

        if (isDemoSession) {
            AuthHelper.clearDemoSession()

            finishLogout(ROUTES_CONFIG.demoLoginUrl)

            return
        }

        signOut()
            .then(() => {
                finishLogout(redirectUrl)
            })
            .catch(e => {
                ToastrHelper.error(e.message)
            })
    }
}
